import { Request } from "./core/olga/Request";
import { UserStorage } from "../shared/tools/UserStorage";
import { Environment } from "../shared/core/Environment";

export class OlgaEnvironmentsAPI {
    static async request({
        endpoint = "/",
        method = "GET",
        headers,
        query,
        data,
    }) {
        return await Request.request({
            endpoint: endpoint,
            method: method,
            headers: headers,
            service: "environments",
            ignoreVersion: true,
            params: query,
            data: data,
        });
    }

    static async requestAuthenticated({
        endpoint = "/",
        method = "GET",
        params,
        data,
    }) {
        return await OlgaEnvironmentsAPI.request({
            endpoint,
            headers: { Authorization: "Bearer " + UserStorage.getAccessToken() },
            method,
            query: params,
            data,
        });
    }

    static async token() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: '/token'
        });
    }

    static async me() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: '/me'
        });
    }

    static async getConfigurations(loading = true) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: '/configurations'
        });
    }

    static async putConfigurations(data, loading = true) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: '/configurations',
            method: 'put',
            data
        });
    }

    static async getUnitConfigurations(unit_id) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/${unit_id}/configurations`
        });
    }

    static async putUnitConfigurations(unit_id, data) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/${unit_id}/configurations`,
            method: 'put',
            data
        });
    }

    static async getWhatsappTemplates(id) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/templates/whatsapp/${id || ""}`,
        });
    }

    static async addSource(unitId, data) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/${unitId}/sources`,
            method: "post",
            data,
        });
    }

    static async setSourceStatus(unitId, sourceId, status) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/${unitId}/sources/${sourceId}/status`,
            method: "put",
            data: { status },
        });
    }

    static async deleteSource(unitId, sourceId) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/${unitId}/sources/${sourceId}`,
            method: "delete",
        });
    }

    static async requestCustomers(paginate = 0, filters) {
        const limit = 18;
        const page = paginate * limit;
        const query = Object.keys(filters || {}).reduce(
            (acc, key) => {
                if (filters[key]) {
                    acc = { ...acc, [key]: filters[key] };
                }
                return acc;
            },
            { limit, offset: page }
        );
        const vars = Object.keys(query).map((key) => `${key}`);

        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: "/customers",
            params: query,
            headers: vars,
        });
    }

    static async requestCustomersId(id) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/customers/${id}`,
        });
    }

    static async getOlgaSources() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: "/sources",
        });
    }

    static async getUnits() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: '/units',
            params: { limit: 9999, offset: 0 }
        });
    }

    static async getSources(unitId) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/${unitId}/sources`
        });
    }

    static async getUsedSources() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/sources/used`
        });
    }

    static async getBrands() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/${Environment.id}/brands`,
        });
    }

    static async getCoupons() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/coupons`,
        });
    }
    
    static async getPartners() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/partners`,
        });
    }

    static async getExternalSourcesConfigs() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/external-sources`,
        });
    }

    static async getExternalSourcesConfigsByAlias(sourceAlias) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/external-sources/${sourceAlias}`,
        });
    }

    static async getAllAvailableExternalSourcesConfigs() {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/external-sources/available`,
        });
    }

    static async getAvailableExternalSourcesConfigs(sourceAlias) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/external-sources/available/${sourceAlias}`,
        });
    }

    static async putExternalSourcesConfigs(sourceAlias, configs) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/units/external-sources/${sourceAlias}`,
            method: "post",
            data: configs,
        });
    }

    static async getEvaluation(environmentId, params) {
        //Remover campos nulos
        const queryParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== null)
        );

        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/evaluation/${environmentId}`,
            params: queryParams,
        });
    }
    
    static async exportEvaluation(environment_id, params) {
        //Remover campos nulos
        const query = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value !== null)
        );

        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/evaluation/${environment_id}/export`,
            params: query,
        });
    }

    static async createWhatsappTemplate(data) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            method: "PUT",
            endpoint: `/templates/whatsapp/`,
            data,
        });
    }

    static async updateWhatsappTemplate(id, data) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            method: "PUT",
            endpoint: `/templates/whatsapp/${id}`,
            data,
        });
    }

    static async deleteWhatsappTemplate(id, data) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            method: "DELETE",
            endpoint: `/templates/whatsapp/${id}`,
            data,
        });
    }

    static async putConfigurationByAlias(alias, data) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/configurations/${alias}`,
            method: "put",
            data,
        });
    }

    static async putExternalMenusConfigs(configs) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: "/configurations/external_menus",
            method: "put",
            data: configs,
        });
    }

    static async postExternalMenusConfigs(configs) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: `/configurations/external_menus`,
            method: "post",
            data: configs,
        });
    }

    static async getAvailableSources(loading = true) {
        return await OlgaEnvironmentsAPI.requestAuthenticated({
            endpoint: '/sources'
        });
    }
}
